import { lazy, Suspense, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import FlowerAuraHeader from '@/components/FlowerAuraHeader';
import FlowerAuraCategories from '@/components/FlowerAuraCategories';
import FlowerAuraProducts from '@/components/FlowerAuraProducts';
import NewArrivals from '@/components/NewArrivals';
import CityIntroParagraph from '@/components/CityIntroParagraph';
import FeaturedFlowerMeanings from '@/components/FeaturedFlowerMeanings';
import TrustBadges from '@/components/TrustBadges';
import Footer from '@/components/Footer';
import CampaignBanner, { MOTHERS_DAY_EGYPT } from '@/components/CampaignBanner';
import CityFAQ from '@/components/CityFAQ';
import SEO from '@/components/ui/SEO';
import { egyptianLocations } from '@/data/locations';

const FlowerAuraSpecialFlowers = lazy(() => import('@/components/FlowerAuraSpecialFlowers'));
const FlowerAuraFlowerTypes = lazy(() => import('@/components/FlowerAuraFlowerTypes'));
const BouquehReviews = lazy(() => import('@/components/BouquehReviews'));
const BouquehTips = lazy(() => import('@/components/BouquehTips'));

const Index = () => {
  const params = useParams<{ citySlug?: string }>();
  const location = useLocation();
  const initialLang = location.pathname.startsWith('/ar-eg/') ? 'ar' : 'en';
  const [language] = useState(initialLang);

  const normalizedPath =
    location.pathname.replace(/\/+$/, '') === '' ? '/' : location.pathname.replace(/\/+$/, '');
  const pathMatch = normalizedPath.match(/^\/(?:en-eg|ar-eg)\/([^/]+)$/);
  const slugCandidate = params.citySlug || pathMatch?.[1];
  const knownCity =
    slugCandidate != null && egyptianLocations.some((c) => c.slug === slugCandidate);
  const resolvedCitySlug = knownCity ? slugCandidate : undefined;

  const cityRecord = resolvedCitySlug
    ? egyptianLocations.find((c) => c.slug === resolvedCitySlug)
    : undefined;
  const cityNameEn = cityRecord?.name_en || cityRecord?.name || '';
  const cityNameAr = cityRecord?.name_ar || cityRecord?.name_en || cityRecord?.name || '';

  // GSC May 2026: city home pages get most shop impressions but CTR is
  // 0.5%. Industry average for position 1-3 is 15-30%. We're losing
  // traffic to AI Overview / featured snippets. Adding commercial hooks
  // (price floor + delivery hook) to the title beats Google's
  // information-only snippet on intent and lifts CTR.
  //
  // Format: "[exact-match commercial intent] [price floor] - [delivery promise] | [brand]"
  // ~57-60 chars in EN — fits Google's SERP truncation limit.
  const pageTitle =
    resolvedCitySlug && cityRecord
      ? language === 'ar'
        ? `ورد في ${cityNameAr} من 500 جنيه - توصيل في نفس اليوم | بوكيه`
        : `Flowers in ${cityNameEn} from 500 EGP - Same-day Delivery | Bouqueh`
      : language === 'ar'
        ? 'ورد أونلاين في مصر من 500 جنيه - توصيل في نفس اليوم | بوكيه'
        : 'Flower Delivery Egypt from 500 EGP - Same Day Before 5 PM | Bouqueh';

  const pageDescription =
    resolvedCitySlug && cityRecord
      ? language === 'ar'
        ? `اطلب باقة ورد فريش في ${cityNameAr} بسعر يبدأ من 500 جنيه. توصيل في نفس اليوم لحد 5 العصر، دفع كاش أو بالكارت، وضمان ورد فريش في كل طلب.`
        : `Order fresh flower bouquets in ${cityNameEn} from 500 EGP. Same-day delivery before 5 PM, cash on delivery or card, fresh-flowers guarantee on every order.`
      : language === 'ar'
        ? 'اطلب باقة ورد فريش أونلاين في مصر من 500 جنيه. توصيل في نفس اليوم لحد 5 العصر، دفع كاش أو بالكارت، وضمان ورد فريش.'
        : 'Order fresh flower bouquets online in Egypt from 500 EGP. Same-day delivery before 5 PM, cash on delivery or card, fresh-flowers guarantee.';

  const langPrefix = language === 'ar' ? 'ar-eg' : 'en-eg';
  const baseUrl = `https://www.bouqueh.com/${langPrefix}`;
  const canonicalUrl = resolvedCitySlug ? `${baseUrl}/${resolvedCitySlug}` : `${baseUrl}/`;
  const cityPath = resolvedCitySlug ? `/${resolvedCitySlug}` : '/';
  const [loadDeferredSections, setLoadDeferredSections] = useState(false);

  useEffect(() => {
    const enableDeferredSections = () => setLoadDeferredSections(true);
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(enableDeferredSections, { timeout: 1500 });
      return () => window.cancelIdleCallback(id);
    }

    const timeoutId = window.setTimeout(enableDeferredSections, 800);
    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        ogTitle={pageTitle}
        ogDescription={pageDescription}
        ogType="website"
        ogImage="https://www.bouqueh.com/og-image.webp"
        ogUrl={canonicalUrl}
        ogLocale={language === 'ar' ? 'ar_EG' : 'en_US'}
        twitterTitle={pageTitle}
        twitterDescription={pageDescription}
        twitterImage="https://www.bouqueh.com/og-image.webp"
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'WebSite',
              '@id': 'https://www.bouqueh.com/#website',
              name: language === 'ar' ? 'بوكيه' : 'Bouqueh',
              description: pageDescription,
              url: 'https://www.bouqueh.com',
              inLanguage: language === 'ar' ? 'ar-EG' : 'en-EG',
              publisher: { '@id': 'https://www.bouqueh.com/#organization' },
              potentialAction: {
                '@type': 'SearchAction',
                target: {
                  '@type': 'EntryPoint',
                  urlTemplate: 'https://www.bouqueh.com/search?q={search_term_string}',
                },
                'query-input': 'required name=search_term_string',
              },
            },
            {
              '@type': 'Organization',
              '@id': 'https://www.bouqueh.com/#organization',
              name: 'Bouqueh',
              url: 'https://www.bouqueh.com',
              logo: 'https://www.bouqueh.com/lovable-uploads/5e9a3006-4daa-4740-a8bf-b7ec5abc7fa3.webp',
              image: 'https://www.bouqueh.com/og-image.webp',
              sameAs: [
                'https://www.facebook.com/bouqueh.official',
                'https://www.instagram.com/bouqueh.official',
                'https://twitter.com/BouquehOfficial',
                'https://www.youtube.com/channel/UCl6SSJjQg6YuEEVuHsOo_rQ',
              ],
            },
            {
              '@type': 'Florist',
              '@id': `${canonicalUrl}#localbusiness`,
              name: resolvedCitySlug && cityRecord
                ? (language === 'ar' ? `بوكيه - ${cityNameAr}` : `Bouqueh - ${cityNameEn}`)
                : (language === 'ar' ? 'بوكيه' : 'Bouqueh'),
              image: 'https://www.bouqueh.com/og-image.webp',
              url: canonicalUrl,
              priceRange: 'EGP 500 - EGP 50000',
              currenciesAccepted: 'EGP',
              paymentAccepted: 'Cash, Credit Card, Visa, Mastercard, Vodafone Cash, InstaPay',
              areaServed: resolvedCitySlug && cityRecord
                ? { '@type': 'City', name: cityNameEn, addressCountry: 'EG' }
                : { '@type': 'Country', name: 'Egypt' },
              address: {
                '@type': 'PostalAddress',
                addressCountry: 'EG',
                addressRegion: resolvedCitySlug && cityRecord ? cityNameEn : 'Egypt',
              },
              openingHoursSpecification: [
                {
                  '@type': 'OpeningHoursSpecification',
                  dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
                  opens: '09:00',
                  closes: '21:00',
                },
              ],
              parentOrganization: { '@id': 'https://www.bouqueh.com/#organization' },
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: '4.9',
                reviewCount: '128',
                bestRating: '5',
                worstRating: '1',
              },
            },
            {
              '@type': 'BreadcrumbList',
              '@id': `${canonicalUrl}#breadcrumb`,
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: language === 'ar' ? 'الرئيسية' : 'Home',
                  item: `https://www.bouqueh.com/${language === 'ar' ? 'ar-eg' : 'en-eg'}/cairo`,
                },
                ...(resolvedCitySlug && cityRecord
                  ? [
                      {
                        '@type': 'ListItem',
                        position: 2,
                        name: language === 'ar' ? cityNameAr : cityNameEn,
                        item: canonicalUrl,
                      },
                    ]
                  : []),
              ],
            },
          ],
        }}
      >
        <html lang={language === 'ar' ? 'ar' : 'en'} />
        <link rel="alternate" hrefLang="ar-eg" href={`https://www.bouqueh.com/ar-eg${cityPath}`} />
        <link rel="alternate" hrefLang="en-eg" href={`https://www.bouqueh.com/en-eg${cityPath}`} />
        <link rel="alternate" hrefLang="x-default" href={`https://www.bouqueh.com/en-eg${cityPath}`} />
      </SEO>
      <div className="min-h-screen bg-white">
        {/* Mother's Day campaign — auto-shows 21 days before March 21 each
            year and hides again the day after. Same template handles
            Valentine's etc. */}
        <CampaignBanner language={language} config={MOTHERS_DAY_EGYPT} />

        <FlowerAuraHeader language={language} />
        <TrustBadges language={language} />
        <FlowerAuraCategories language={language} />
        <FlowerAuraProducts language={language} />
        {/* New Arrivals — fresh-content signal + ~8 more H3 product names
            in the prerender for SEO depth. See Floward comparison audit. */}
        <NewArrivals language={language} />

        {loadDeferredSections ? (
          <Suspense fallback={<div className="py-12" />}>
            <FlowerAuraSpecialFlowers language={language} />
            <FlowerAuraFlowerTypes language={language} />
            <BouquehReviews language={language} />
            <BouquehTips />
          </Suspense>
        ) : (
          <div className="py-12" />
        )}

        {/* City guides — always rendered (in bot HTML) to give the guides hub strong internal links */}
        {resolvedCitySlug && (
          <section className="py-12 bg-muted/30">
            <div className="container mx-auto px-4 max-w-5xl">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2 text-center">
                {language === 'ar' ? `أدلة الورد في ${cityNameAr}` : `Flower Guides for ${cityNameEn}`}
              </h2>
              <p className="text-muted-foreground text-center mb-8">
                {language === 'ar' ? 'كل ما تحتاج معرفته قبل الطلب' : 'Everything you need to know before you order'}
              </p>
              <ul className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3">
                <li>
                  <Link to={`/${langPrefix}/${resolvedCitySlug}/guides/how-to-buy-flowers`} className="block p-4 bg-white rounded-lg border hover:border-primary text-sm font-medium text-center transition-colors">
                    {language === 'ar' ? `كيفية شراء الورد في ${cityNameAr}` : `How to buy flowers in ${cityNameEn}`}
                  </Link>
                </li>
                <li>
                  <Link to={`/${langPrefix}/${resolvedCitySlug}/guides/send-flowers`} className="block p-4 bg-white rounded-lg border hover:border-primary text-sm font-medium text-center transition-colors">
                    {language === 'ar' ? `إرسال الورد إلى ${cityNameAr}` : `Send flowers to ${cityNameEn}`}
                  </Link>
                </li>
                <li>
                  <Link to={`/${langPrefix}/${resolvedCitySlug}/guides/best-florists`} className="block p-4 bg-white rounded-lg border hover:border-primary text-sm font-medium text-center transition-colors">
                    {language === 'ar' ? `أفضل محل ورد في ${cityNameAr}` : `Best florist in ${cityNameEn}`}
                  </Link>
                </li>
                <li>
                  <Link to={`/${langPrefix}/${resolvedCitySlug}/guides/best-areas`} className="block p-4 bg-white rounded-lg border hover:border-primary text-sm font-medium text-center transition-colors">
                    {language === 'ar' ? `أفضل أحياء ${cityNameAr}` : `Best areas in ${cityNameEn}`}
                  </Link>
                </li>
              </ul>
              <p className="text-center mt-6">
                <Link to={`/${langPrefix}/${resolvedCitySlug}/guides`} className="text-primary hover:underline">
                  {language === 'ar' ? `جميع أدلة ${cityNameAr} ←` : `All ${cityNameEn} guides →`}
                </Link>
              </p>
            </div>
          </section>
        )}

        {/* City-specific long-form intro — placed below the guides as the
            final piece of body content (the way Floward/Floom structure
            their city pages). Still part of the prerendered HTML, still
            mentions 6 real area names, still bilingual. */}
        <CityIntroParagraph language={language} />

        {/* 4 flower-meaning cards — internal link signal toward the glossary
            hub from every city home. Placed above the FAQ so the page closes
            with the FAQ section directly before the footer. */}
        <FeaturedFlowerMeanings language={language} />

        {/* FAQ accordion + FAQPage JSON-LD — answers common buyer questions
            in-context and makes the page eligible for FAQ rich snippets in
            SERPs. City-aware so each city home's FAQ is unique. Now sits
            as the final section before the footer. */}
        {resolvedCitySlug && cityRecord && (
          <CityFAQ language={language} cityName={language === 'ar' ? cityNameAr : cityNameEn} />
        )}

        <Footer language={language} />
      </div>
    </>
  );
};

export default Index;
